import React, { useState } from 'react';
import { Modal } from '@shared/components/ui/Modal';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { AlertCircle, CheckCircle, Loader, Bug, Lightbulb, MessageSquare, HelpCircle } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type FeedbackType = 'bug_report' | 'feature_request' | 'general_feedback' | 'other';
type Severity = 'low' | 'medium' | 'high' | 'critical';

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('general_feedback');
  const [severity, setSeverity] = useState<Severity>('medium');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim() || !description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to submit feedback');
        return;
      }

      const { error } = await supabase
        .from('feedback_submissions')
        .insert({
          user_id: user.id,
          feedback_type: feedbackType,
          severity: severity,
          subject: subject.trim(),
          description: description.trim(),
          page_url: window.location.href,
          user_email: email.trim() || user.email,
          status: 'new'
        });

      if (error) throw error;

      setSubmitted(true);
      toast.success('Feedback submitted successfully!');

      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFeedbackType('general_feedback');
    setSeverity('medium');
    setSubject('');
    setDescription('');
    setEmail('');
    setSubmitted(false);
    onClose();
  };

  const getFeedbackTypeIcon = (type: FeedbackType) => {
    switch (type) {
      case 'bug_report':
        return <Bug className="w-5 h-5" />;
      case 'feature_request':
        return <Lightbulb className="w-5 h-5" />;
      case 'general_feedback':
        return <MessageSquare className="w-5 h-5" />;
      case 'other':
        return <HelpCircle className="w-5 h-5" />;
    }
  };

  const getSeverityColor = (sev: Severity) => {
    switch (sev) {
      case 'critical':
        return 'bg-red-100 border-red-300 text-red-800';
      case 'high':
        return 'bg-orange-100 border-orange-300 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'low':
        return 'bg-blue-100 border-blue-300 text-blue-800';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Send Feedback">
      {submitted ? (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Thank You!
          </h3>
          <p className="text-gray-600">
            Your feedback has been submitted successfully. We appreciate your input!
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Feedback Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'bug_report' as FeedbackType, label: 'Bug Report' },
                { value: 'feature_request' as FeedbackType, label: 'Feature Request' },
                { value: 'general_feedback' as FeedbackType, label: 'General Feedback' },
                { value: 'other' as FeedbackType, label: 'Other' }
              ].map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFeedbackType(type.value)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                    feedbackType === type.value
                      ? 'border-blue-600 bg-blue-50 text-blue-900'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {getFeedbackTypeIcon(type.value)}
                  <span className="text-sm font-medium">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {feedbackType === 'bug_report' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Severity
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { value: 'low' as Severity, label: 'Low' },
                  { value: 'medium' as Severity, label: 'Medium' },
                  { value: 'high' as Severity, label: 'High' },
                  { value: 'critical' as Severity, label: 'Critical' }
                ].map((sev) => (
                  <button
                    key={sev.value}
                    type="button"
                    onClick={() => setSeverity(sev.value)}
                    className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                      severity === sev.value
                        ? getSeverityColor(sev.value)
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {sev.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
              Subject <span className="text-red-500">*</span>
            </label>
            <Input
              id="subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of your feedback"
              required
              disabled={submitting}
              maxLength={200}
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please provide detailed information about your feedback or issue..."
              required
              disabled={submitting}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">
              {description.length}/2000 characters
            </p>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Email (optional)
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              disabled={submitting}
            />
            <p className="text-xs text-gray-500 mt-1">
              We'll use this if we need to follow up on your feedback
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Privacy Note</p>
                <p>
                  Your feedback will be reviewed by our team. We capture the current page URL
                  to help us understand the context of your feedback.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !subject.trim() || !description.trim()}
              className="flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};
